import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAuthnLoginUrl } from '@centrix/types';

// These are set to their defaults here and overridden by configure() called from each
// app's main.tsx. Reading import.meta.env inside a shared package defeats Vite's static
// analysis (the cast to unknown breaks inlining), so env vars are passed in explicitly.
let API_BASE = '/api/centrix/v1';
// LMS origin for native OpenEdX endpoints (/xblock, /courses, /assets).
// In dev these are proxied by Vite to the same host, so '' works fine.
// In production the LMS lives on a different origin (e.g. centrix-dev.learnovate360.com)
// while the MFEs are on apps.centrix-dev.learnovate360.com — we need the explicit origin.
let LMS_BASE = '';
let _env: Record<string, string> = {};

export function configure(config: {
  apiBaseUrl?: string;
  lmsBaseUrl?: string;
  env?: Record<string, string>;
}) {
  if (config.apiBaseUrl) API_BASE = config.apiBaseUrl;
  if (config.lmsBaseUrl) LMS_BASE = config.lmsBaseUrl;
  if (config.env) _env = config.env;
}

/**
 * The configured LMS origin for native OpenEdX content. Empty in dev (where Vite
 * proxies relative paths), an explicit origin otherwise. Use it to make a
 * root-relative LMS path absolute for browser-native loads (iframes, anchors)
 * that don't go through the axios interceptor.
 */
export function getLmsBaseUrl(): string {
  return LMS_BASE;
}

/**
 * True when running under a Vite dev server (per the configured env). Used to
 * route browser-native LMS asset loads through the same-origin dev proxy
 * (/lms-media, /lms-asset) instead of the cross-origin LMS origin.
 */
export function isDevEnv(): boolean {
  return Boolean(_env.DEV);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getCsrfTokenFromResponse(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null;
  const token = (data as { csrfToken?: unknown }).csrfToken;
  return typeof token === 'string' && token.length > 0 ? token : null;
}

let csrfTokenCache: string | null = null;

interface GetCsrfTokenOptions {
  forceRequest?: boolean;
}

function getCsrfTokenFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

async function requestCsrfToken(): Promise<string> {
  try {
    const response = await axios.get<{ csrfToken?: string } | undefined>(`${API_BASE}/auth/csrf/`, {
      withCredentials: true,
    });

    const token = getCsrfTokenFromResponse(response.data) ?? getCsrfTokenFromCookie();
    if (!token) throw new Error('CSRF token not available');

    csrfTokenCache = token;
    return token;
  } catch (error) {
    const cookieToken = getCsrfTokenFromCookie();
    if (cookieToken) {
      csrfTokenCache = cookieToken;
      return cookieToken;
    }
    if (csrfTokenCache) return csrfTokenCache;
    if (axios.isAxiosError(error)) throw toApiError(error);
    throw error;
  }
}

export async function getCsrfToken(options: GetCsrfTokenOptions = {}): Promise<string> {
  if (!options.forceRequest) {
    if (csrfTokenCache) return csrfTokenCache;
    // Use the cookie directly if available — avoids an extra round-trip and works
    // when the csrftoken cookie is readable on the current domain (same-domain setups).
    const cookieToken = getCsrfTokenFromCookie();
    if (cookieToken) {
      csrfTokenCache = cookieToken;
      return cookieToken;
    }
  }
  return requestCsrfToken();
}

export function clearCsrfTokenCache(): void {
  csrfTokenCache = null;
}

const apiClient = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

const MUTATING_METHODS = ['post', 'put', 'patch', 'delete'];

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // For FormData uploads, drop the default JSON Content-Type so axios/the browser
  // can set `multipart/form-data; boundary=...` automatically.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  // FOR NATIVE APIs — route to LMS origin instead of the Centrix API base.
  // Excludes /courses/<key>/metadata/ so course metadata CRUD goes through the
  // normal /api/centrix/v1/ base and hits the correct endpoint.
  // Also excludes /courses/<key>/certificate/(activate|deactivate)/ — the
  // per-course certificate register lives under the Centrix API base, NOT the
  // native LMS origin; rerouting it strips the /api/centrix/v1 prefix and 404s.
  // In dev LMS_BASE is '' so relative paths are proxied by Vite.
  // In production LMS_BASE is the explicit LMS origin (e.g. https://lms.example.com)
  // which differs from the MFE origin (e.g. https://apps.example.com).
  const url = config.url ?? '';
  const isCourseMetadata = url.startsWith('/courses/') && url.endsWith('/metadata/');
  const isCourseCertificate = url.startsWith('/courses/') && url.includes('/certificate/');
  if (
    !url.includes('purchase') &&
    !url.includes('prerequisites') &&
    !isCourseMetadata &&
    !isCourseCertificate &&
    (url.startsWith('/api') ||
      url.startsWith('/xblock') ||
      url.startsWith('/courses/') ||
      url.startsWith('/assets/'))
  )
    config.baseURL = LMS_BASE;
  if (config.method && MUTATING_METHODS.includes(config.method.toLowerCase())) {
    try {
      const token = await getCsrfToken();
      config.headers.set('X-CSRFToken', token);
    } catch {
      // Continue without CSRF token — server will reject if required
    }
  }
  return config;
});

let isRefreshing = false;
let isRedirectingToLogin = false;
let refreshSubscribers: Array<(success: boolean) => void> = [];

function subscribeToRefresh(cb: (success: boolean) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshComplete(success: boolean) {
  refreshSubscribers.forEach((cb) => cb(success));
  refreshSubscribers = [];
}

function redirectToLogin(): never {
  // The backend clears cookies on a failed refresh response — no need to call
  // /auth/logout/ here. Calling it races with the refresh and can blacklist the
  // refresh token before the retry completes, causing re-login loops.
  clearCsrfTokenCache();
  if (typeof window !== 'undefined') {
    // Do not pass the current URL as `next` — if the session is dead, routing
    // back to the same page would re-trigger the refresh → login loop.
    window.location.href = getAuthnLoginUrl(undefined, {
      env: _env,
      isDev: Boolean(_env.DEV),
    });
    if ((import.meta as { env?: { MODE?: string } }).env?.MODE !== 'test') {
      return new Promise(() => {}) as never;
    }
  }
  return Promise.reject(new ApiError(401, 'Session expired')) as never;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // 403 with a CSRF error message — cached token is stale. Clear cache, read
    // the current csrftoken cookie (the ground truth Django validates against),
    // and retry the request once. The _csrfRetried flag on the config prevents
    // an infinite loop if the cookie itself is missing or invalid.
    const cfg = originalRequest as (typeof originalRequest & { _csrfRetried?: boolean }) | null;
    if (cfg && !cfg._csrfRetried && error.response?.status === 403) {
      const detail = (error.response.data as Record<string, unknown>)?.detail;
      if (typeof detail === 'string' && detail.toLowerCase().includes('csrf')) {
        cfg._csrfRetried = true;
        clearCsrfTokenCache();
        try {
          const token = await getCsrfToken({ forceRequest: true });
          cfg.headers.set('X-CSRFToken', token);
          return apiClient(cfg);
        } catch {
          return Promise.reject(toApiError(error));
        }
      }
    }

    if (!originalRequest || error.response?.status !== 401) {
      return Promise.reject(toApiError(error));
    }

    const isAuthEndpoint = originalRequest.url?.includes('/auth/');
    if (isAuthEndpoint) {
      return Promise.reject(toApiError(error));
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeToRefresh((success) => {
          if (success) {
            resolve(apiClient(originalRequest));
          } else {
            reject(toApiError(error));
          }
        });
      });
    }

    isRefreshing = true;
    try {
      await axios.post(`${API_BASE}/auth/refresh/`, null, { withCredentials: true });
      clearCsrfTokenCache();
      isRefreshing = false;
      onRefreshComplete(true);
      return apiClient(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      onRefreshComplete(false);

      const refreshData = axios.isAxiosError(refreshError)
        ? (refreshError.response?.data as Record<string, unknown> | undefined)
        : undefined;
      const refreshCode = refreshData?.code;

      // token_expired / token_invalid means the session is definitively over.
      // The backend clears cookies on the refresh response, so no logout call needed.
      const isTokenExpired = refreshCode === 'token_expired' || refreshCode === 'token_invalid';

      if (isTokenExpired && !isRedirectingToLogin) {
        isRedirectingToLogin = true;
        return redirectToLogin();
      }

      // For any other refresh failure, redirect without calling logout —
      // calling logout races with the refresh and can cause re-login loops.
      if (typeof window !== 'undefined' && !isRedirectingToLogin) {
        isRedirectingToLogin = true;
        return redirectToLogin();
      }
      return Promise.reject(toApiError(error));
    }
  }
);

function flattenStrings(value: unknown): string[] {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.flatMap(flattenStrings);
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap(flattenStrings);
  }
  return [];
}

function extractDrfErrorMessage(data: unknown): string | null {
  if (data == null) return null;
  if (typeof data === 'string') {
    const trimmed = data.trim();
    // Some backends return an HTML error page (e.g. a Django 500 debug page) as
    // the response body. Rendering that verbatim floods the UI, so treat it as
    // having no usable message and let the caller fall back to a generic one.
    if (trimmed.startsWith('<')) return null;
    return trimmed || null;
  }

  // DRF top-level array (e.g. ["This user already has this role at enterprise level."])
  if (Array.isArray(data)) {
    const messages = flattenStrings(data);
    return messages.length > 0 ? messages.join(' ') : null;
  }

  if (typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;

  // Standard DRF error fields take precedence
  if (typeof obj.detail === 'string') return obj.detail;
  if (typeof obj.message === 'string') return obj.message;
  if (typeof obj.error === 'string') return obj.error;
  if (Array.isArray(obj.non_field_errors)) {
    const messages = flattenStrings(obj.non_field_errors);
    if (messages.length > 0) return messages.join(' ');
  }

  // Fall back to field-level errors (e.g. { email: ["This field is required."] })
  const messages = flattenStrings(obj);
  return messages.length > 0 ? messages.join(' ') : null;
}

function toApiError(error: AxiosError): ApiError {
  const status = error.response?.status ?? 0;
  const data = error.response?.data;
  const message = extractDrfErrorMessage(data) || error.message || 'Request failed';
  return new ApiError(status, message, data);
}

export { apiClient };
export default apiClient;
